'use client'

import { AudioWaveform, Headset, Layers, UserRoundCheck, Workflow } from 'lucide-react'
import './home-v2.css'
import './backstage.css'

// "What we handle for you", staged as the backstage: a deep-navy band where
// the five things an agency hands over sit as quiet glass panels. Each has
// one precise line icon in a bracketed frame (no gradient tiles, no cartoon
// loops). AI agents is the single feature panel, with a voice waveform,
// because AI call agents are a real product we deploy. Copy carried over
// unchanged from the previous version of this section.
const CARDS = [
  {
    area: 'a',
    icon: UserRoundCheck,
    tag: 'On demand',
    title: 'No GHL Expert on Your Team',
    text: 'Stop paying for a full-time hire you barely need. Tap certified GHL specialists on demand for builds, fixes, strategy, or anything in between.',
  },
  {
    area: 'b',
    icon: Layers,
    tag: 'White-label',
    title: "Can't Launch Your SaaS Fast Enough",
    text: 'We set up your fully white-labeled CRM from scratch: branded, configured, and client-ready. You own the product. We do the build.',
  },
  {
    area: 'c',
    icon: Workflow,
    tag: 'Automation',
    title: 'Broken or Half-Built Automations',
    text: 'Leaking leads, missed follow-ups, workflows that randomly break. We build, audit, and fix automations end-to-end, so every lead is handled perfectly.',
  },
  {
    area: 'd',
    icon: AudioWaveform,
    tag: 'AI agents',
    title: 'AI Agents That Actually Work for Your Agency',
    text: "We design, build, and deploy AI agents tailored to your agency's workflow. Qualify leads, handle inquiries, run AI call centers, and book meetings 24/7, without you touching a thing.",
    feature: true,
  },
  {
    area: 'e',
    icon: Headset,
    tag: '24/7',
    title: 'No Support = Clients Leave Your Platform',
    text: "Your clients expect fast answers. We provide round-the-clock GHL expert support, fully under your brand. They think it's your team; we make you look like a well-staffed operation.",
  },
]

// Bar heights for the waveform: a speech-like envelope, fixed so the server
// and client render the same markup.
const WAVE = Array.from({ length: 36 }, (_, i) => {
  const envelope = Math.sin((Math.PI * (i + 0.5)) / 36)
  const voice = 0.45 + 0.55 * Math.abs(Math.sin(i * 0.9) * Math.cos(i * 0.37))
  return Math.max(0.12, +(envelope * voice).toFixed(3))
})

// The panel's spotlight follows the pointer (mouse only; touch has no hover).
function trackPointer(event) {
  if (event.pointerType !== 'mouse') return
  const card = event.currentTarget
  const rect = card.getBoundingClientRect()
  card.style.setProperty('--mx', `${event.clientX - rect.left}px`)
  card.style.setProperty('--my', `${event.clientY - rect.top}px`)
}

export default function BentoV2() {
  return (
    <section className="hv2 hv2-section bk">
      <div className="hv2-inner">
        <div className="hv2-head centered" data-rv="0.4">
          <span className="hv2-eyebrow">What we handle for you</span>
          <h2>You Get the Experts. We Work <span className="hv2-hl">Behind the Scenes.</span></h2>
          <p>
            From white-label launch support to automation builds, AI deployment, and client
            support, we become the technical team your agency can rely on.
          </p>
        </div>

        <div className="bk-grid">
          {CARDS.map((card) => {
            const Icon = card.icon
            return (
              <article
                key={card.title}
                className={`bk-card bk-${card.area}${card.feature ? ' is-feature' : ''}`}
                onPointerMove={trackPointer}
              >
                <div className="bk-card-top">
                  <span className="bk-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <span className="bk-tag">{card.tag}</span>
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
                {card.feature ? (
                  <div className="bk-wave" aria-hidden="true">
                    {WAVE.map((h, i) => (
                      <span key={i} style={{ '--h': h, '--i': i }} />
                    ))}
                  </div>
                ) : null}
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
